import { Form, InputNumber, Modal, Space } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import _ from 'lodash';
import React, { ReactElement, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import ILoanTransaction from '~Api/Loan/ILoanTransaction';
import { loanRecordOtherRetainedFundsReductionAction } from '~Loans/actions';
import { loanTransactionsSelector } from '~Loans/selectors';
import DatePicker from '~UI/DatePicker';
import TimePicker from '~UI/TimePicker';
import constants from '~constants';
import { IGlobalState } from '~reducer';
import { IDictionary } from '~utilities/IDictionary';

export default function RecordOtherRetainedFundsReductionModal(props: {
    loanUuid: string;
    isOpen: boolean;
    onCancel: () => void;
}): ReactElement {
    const {
        loanUuid,
        isOpen,
        onCancel,
    } = props;

    const [error, setError] = useState<string>(null);
    const [otherRetainedFunds, setOtherRetainedFunds] = useState<number>(null);
    const [transactionDateTime, setTransactionDateTime] = useState<string>(null);

    const loanTransactions: IDictionary<ILoanTransaction> = useSelector((state: IGlobalState) => loanTransactionsSelector(state, loanUuid));

    const dispatch: Dispatch = useDispatch();

    const onChangeDate: (date: Dayjs) => void = useCallback((date: Dayjs) => {
        setTransactionDateTime(dayjs(`${date.format('YYYY-MM-DD')} ${dayjs(transactionDateTime || undefined).format('HH:mm')}`).format());
    }, [
        transactionDateTime,
    ]);

    const onChangeTime: (time: Dayjs) => void = useCallback((time: Dayjs) => {
        setTransactionDateTime(dayjs(`${dayjs(transactionDateTime || undefined).format('YYYY-MM-DD')} ${time.format('HH:mm')}`).format());
    }, [
        transactionDateTime,
    ]);

    const onChangeOtherRetainedFunds: (value: number) => void = useCallback((value: number) => {
        setOtherRetainedFunds(value);
    }, [
        loanTransactions,
    ]);

    const validateOtherRetainedFundsAmount: () => boolean = useCallback(() => {
        let error: string = null;

        let otherRetainedFundsBalance = 0;
        _.each(_.sortBy(loanTransactions, ['transactionTime']), (loanTransaction: ILoanTransaction) => {
            if (loanTransaction.otherRetainedFunds !== 0) {
                otherRetainedFundsBalance += loanTransaction.otherRetainedFunds;
            }
        });

        if (!otherRetainedFunds) {
            error = 'Please enter an amount';
        }
        else if (otherRetainedFundsBalance < otherRetainedFunds) {
            error = 'Amount exceeds available balance';
        }

        setError(error);

        return !error;
    }, [
        otherRetainedFunds,
        loanTransactions,
    ]);

    const transactionDateTimeDayjs: Dayjs = dayjs(transactionDateTime || undefined);

    const onClickRecord: () => void = useCallback(() => {
        let valid: boolean = true;

        valid = validateOtherRetainedFundsAmount() && valid;

        if (!valid) {
            return;
        }

        dispatch(loanRecordOtherRetainedFundsReductionAction(loanUuid, otherRetainedFunds, transactionDateTimeDayjs.format(constants.DATE_ISO_FORMAT)));
        onCancel();
    }, [
        dispatch,
        loanUuid,
        onCancel,
        otherRetainedFunds,
        transactionDateTimeDayjs,
        validateOtherRetainedFundsAmount,
    ]);

    return (
        <Modal
            okText='Record'
            onCancel={onCancel}
            onOk={onClickRecord}
            open={isOpen}
            title='Other Retained Funds Reduction'
            wrapClassName='loan-record-other-retained-funds-reduction-modal'
        >
            <Form.Item
                label='Transaction Date'
                className='transaction-date'
            >
                <Space>
                    <DatePicker
                        className='transaction-pick-date'
                        onChange={onChangeDate}
                        value={transactionDateTimeDayjs || dayjs()}
                        format='DD/MM/YYYY'
                    />
                    <TimePicker
                        className='transaction-pick-time'
                        onChange={onChangeTime}
                        value={transactionDateTimeDayjs || dayjs()}
                        format='H:mm'
                    />
                </Space>
            </Form.Item>
            <Form.Item className='amount' label='Other Retained Funds' help={error} validateStatus={error && 'error'}>
                <InputNumber addonBefore='$' min={0} onBlur={validateOtherRetainedFundsAmount} onChange={onChangeOtherRetainedFunds} type='number' value={otherRetainedFunds} />
            </Form.Item>
        </Modal>
    );
}
